import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../../components/shared/Card';
import { Select } from '../../../../components/shared/inputs';
import { Input } from '../../../../components/shared/inputs';
import { SortGroupsList } from './SortGroupsList';
import { MediumGrey } from '../../../../theme';

interface GroupItem {
  id: string;
  groupName: string;
  taps: any[];
  index: number;
}

interface ContentSectionProps {
  title: string;
  onTitleChange: (value: string) => void;
  sortBy: string;
  onSortByChange: (value: string) => void;
  groupBy: string;
  onGroupByChange: (value: string) => void;
  columns: number;
  onColumnsChange: (value: number) => void;
  columnOptions?: number[];
  tapStart: number;
  onTapStartChange: (value: number) => void;
  tapEnd: number;
  onTapEndChange: (value: number) => void;
  isGrouped: boolean;
  groupOrder: GroupItem[];
  onDragEnd: (param: any) => void;
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  title,
  onTitleChange,
  sortBy,
  onSortByChange,
  groupBy,
  onGroupByChange,
  columns,
  onColumnsChange,
  columnOptions = [1, 2, 3],
  tapStart,
  onTapStartChange,
  tapEnd,
  onTapEndChange,
  isGrouped,
  groupOrder,
  onDragEnd
}) => {
  const { t } = useTranslation();

  return (
    <Card className="border border-gray-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t('PrintDialog.Basics')}</h2>
      <p className="text-sm text-gray-500 mb-4">{t('PrintDialog.ContentSectionHelper')}</p>

      <div className="space-y-4">
        <Input label={t('PrintDialog.MenuTitle')} value={title} onChange={(v) => onTitleChange(String(v ?? ''))} />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label={t('PrintDialog.SortBy')}
            value={sortBy}
            onChange={(value) => onSortByChange(String(value))}
            options={[
              { value: 'handle', label: t('PrintDialog.TapHandleNumber') },
              { value: 'abv', label: t('PrintDialog.BeverageABV') },
              { value: 'style', label: t('PrintDialog.Style') },
              { value: 'category', label: t('PrintDialog.Category') },
              { value: 'breweryName', label: t('PrintDialog.BreweryName') },
              { value: 'beverageName', label: t('PrintDialog.BeverageName') }
            ]}
          />
          <Select
            label={t('PrintDialog.GroupBy')}
            value={groupBy}
            onChange={(value) => onGroupByChange(String(value))}
            options={[
              { value: 'none', label: t('PrintDialog.None') },
              { value: 'style', label: t('PrintDialog.Style') },
              { value: 'category', label: t('PrintDialog.Category') },
              { value: 'brewery', label: t('PrintDialog.Brewery') }
            ]}
          />
        </div>

        <Select
          label={t('PrintDialog.Columns')}
          value={columns}
          onChange={(value) => onColumnsChange(Number(value))}
          options={columnOptions.map((n) => ({ value: n, label: String(n) }))}
        />

        <div className="pt-2">
          <h3 className="text-base font-medium text-gray-700 mb-3">{t('PrintDialog.TapList')}</h3>
          <div className="grid grid-cols-3 gap-4">
            <Input label={t('PrintDialog.TapNoStart')} type="number" value={tapStart} onChange={(v) => onTapStartChange(Number(v ?? 1))} />
            <Input label={t('PrintDialog.TapNoEnd')} type="number" value={tapEnd} onChange={(v) => onTapEndChange(Number(v ?? 1))} />
            <Input label={t('PrintDialog.TapsShown')} type="number" disabled value={tapEnd - tapStart + 1 || 0} />
          </div>
        </div>

        {isGrouped && (
          <div className="pt-2">
            <h3 className="text-base font-medium text-gray-700 mb-1">{t('PrintDialog.SortGroups')}</h3>
            <p className="text-sm mb-3" style={{ color: MediumGrey }}>
              {t('PrintDialog.SelectGroupOrder')}
            </p>
            <SortGroupsList groupOrder={groupOrder} onDragEnd={onDragEnd} />
          </div>
        )}
      </div>
    </Card>
  );
};
